import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff, User, Mail, Phone, Lock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { useUser } from '../context/UserContext';
import { useSettings } from '../context/SettingsContext';
import { useToast } from '../hooks/use-toast';
import { useRecaptcha } from '../hooks/useRecaptcha';
import usePageSEO from '../hooks/usePageSEO';
import { getLocalizedSiteName } from '../lib/siteName';
import { isStrongPassword } from '../lib/password';

const Register = () => {
  const { t, i18n  } = useTranslation();
  const navigate = useNavigate();
  const { register } = useUser();
  const { settings } = useSettings();
  const { toast } = useToast();
  const { execute: executeRecaptcha, enabled: recaptchaEnabled } = useRecaptcha();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', password: '', confirmPassword: '' });
  const lang = i18n.language?.substring(0, 2) || 'en';
  const siteName = getLocalizedSiteName(settings, lang);

  usePageSEO({ title: t('auth.createAccount'), noIndex: true });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirmPassword) {
      toast({ title: t('auth.error'), description: t('auth.passwordsDoNotMatch'), variant: 'destructive' });
      return;
    }
    if (!isStrongPassword(form.password)) {
      toast({ title: t('auth.error'), description: t('auth.passwordComplexity'), variant: 'destructive' });
      return;
    }
    setLoading(true);
    try {
      let recaptchaToken = '';
      if (settings.recaptcha_site_key && window.grecaptcha) {
        recaptchaToken = await window.grecaptcha.execute(settings.recaptcha_site_key, { action: 'register' });
      }
      await register(form.name, form.email, form.phone, form.password, recaptchaToken);
      toast({ title: t('auth.createAccount'), description: t('auth.checkEmailToVerify', { name: siteName }) });
      navigate('/login', { state: { verificationEmail: form.email } });
    } catch (error) {
      const msg = error.response?.data?.detail || 'Registration failed';
      toast({ title: 'Error', description: msg, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-16 flex items-center justify-center bg-gray-50" data-testid="register-page">
      <Card className="w-full max-w-md mx-4 shadow-xl">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold">{t('auth.createAccount')}</CardTitle>
          <p className="text-gray-500 text-sm mt-1">{t('auth.joinUs', { name: siteName })}</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>{t('auth.fullName')}</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input data-testid="register-name" className="pl-10" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
              </div>
            </div>
            <div>
              <Label>{t('auth.email')}</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input data-testid="register-email" type="email" className="pl-10" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
              </div>
            </div>
            <div>
              <Label>{t('auth.phone')}</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input data-testid="register-phone" className="pl-10" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+973 1234 5678" />
              </div>
            </div>
            <div>
              <Label>{t('auth.password')}</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input data-testid="register-password" type={showPassword ? 'text' : 'password'} className="pl-10 pr-10" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required placeholder={t('auth.passwordMinLength')} />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div>
              <Label>{t('auth.confirmPassword')}</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input data-testid="register-confirm" type="password" className="pl-10" value={form.confirmPassword} onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })} required placeholder={t('auth.repeatPassword')} />
              </div>
            </div>
            <Button type="submit" className="w-full bg-red-600 hover:bg-red-700" disabled={loading} data-testid="register-submit">
              {loading ? t('auth.creatingAccount') : t('auth.createAccountBtn')}
            </Button>
            {settings.recaptcha_site_key && (
              <p className="text-xs text-gray-400 text-center mt-2" data-testid="recaptcha-notice">
                Protected by Google <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="underline">reCAPTCHA</a>
              </p>
            )}
          </form>
          <p className="text-center text-sm text-gray-500 mt-6">
            {t('auth.alreadyHaveAccount')} <Link to="/login" className="text-red-600 font-semibold hover:underline">{t('auth.signIn')}</Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default Register;
